import * as React from 'react'
import { FC, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { DraftLead } from '@user/store/draftLeads/typings'
import { LightTooltip, MoreButton, toast } from '@sales-center/components'
import { timeFormat } from '@sales-center/common'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { deleteDraftLead } from '@user/store/draftLeads'
import { permissionDenied } from '@user/store/authorization/permissions'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { branchUnmapped } from '@user/store/draftLeads/constants'
import { useLocalizationDateFormat } from '@user/features/Localization/useLocalizationDateFormat'
import { useTheme } from '@mui/material/styles'

interface Props {
  lead: DraftLead
  canDelete: boolean
}

const styles = {
  label: {
    fontFamily: 'Source Sans Pro Semibold',
  },
}

export const DraftLeadHeader: FC<Props> = (props) => {
  const { lead, canDelete } = props
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const { formatDate } = useLocalizationDateFormat()

  const openDraftInAddLeadPage = useCallback(() => {
    navigate(`/leads/addLead?draftId=${lead.id}`)
  }, [lead.id, navigate])

  const dateSubmittedFormat = React.useMemo(
    () => formatDate(lead.createdTime) + ' ' + timeFormat(lead.createdTime),
    [lead.createdTime, formatDate],
  )

  const onDeletePendingLead = useCallback(async () => {
    const result = await dispatch(deleteDraftLead(lead.id))

    if (deleteDraftLead.fulfilled.match(result)) {
      toast.success('Record deleted successfully')
    } else {
      if (result.payload) toast.error(result.payload.Message)
    }
  }, [dispatch, lead.id])
  const theme = useTheme()
  return (
    <Box display="flex" alignItems="center">
      <Box flexGrow={1}>
        <Typography variant="caption">
          <span style={styles.label}>Date Submitted:</span>{' '}
          {dateSubmittedFormat}
        </Typography>
      </Box>
      <Box>
        {lead.isBranchMapped || lead.branchId === null ? (
          <Button
            style={{ marginRight: theme.spacing(0.5) }}
            color="primary"
            disableElevation
            onClick={openDraftInAddLeadPage}
            variant="contained"
            data-test-id={`review-pending-lead-${lead.id}`}
          >
            Review
          </Button>
        ) : (
          <LightTooltip title={branchUnmapped}>
            <span>
              <Button
                style={{ marginRight: theme.spacing(0.5) }}
                color="primary"
                disableElevation
                onClick={openDraftInAddLeadPage}
                variant="contained"
                disabled={true}
                data-test-id={`review-pending-lead-${lead.id}`}
              >
                Review
              </Button>
            </span>
          </LightTooltip>
        )}

        {
          <MoreButton
            dataTestId={`draft-lead-more-${lead.id}`}
            options={[
              {
                label: 'Delete',
                onClick: onDeletePendingLead,
                disabled: !canDelete,
                tooltipText: canDelete ? undefined : permissionDenied,
                dataTestId: `delete-pending-lead-${lead.id}`,
              },
            ]}
          />
        }
      </Box>
    </Box>
  )
}

DraftLeadHeader.displayName = 'DraftLeadHeader'
